import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  Grid,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import { ADMIN_PATH } from '../../../../url';
import ErrorSwitchCase from '../../../../services/ErrorHandlerService';
import HttpService from '../../../../services/HttpService';
import { useDispatch } from 'react-redux';
import { SHOW_NOTIFICATION } from '../../../../constants';
import dayjs from 'dayjs';

const AddOfficeLocationComponent = (props) => {
  const {
    office,
    expanded,
    setExpanded,
    editable,
    setEditable,
    loadData,
    resetOffice,
  } = props;

  const [loadingApiCall, setLoadingApiCall] = useState(false);
  const [locationName, setLocationName] = useState(office.locationName);
  const [openingDate, setOpeningDate] = useState(office.openingDate);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState(office.address);
  const [postalCode, setPostalCode] = useState(office.postalCode);
  const [city, setCity] = useState(office.city);
  const [state, setState] = useState(office.state);
  const [country, setCountry] = useState(office.country);
  const [officeType, setOfficeType] = useState(office.officeType);

  const [locationNameError, setLocationNameError] = useState(false);
  const [isDateValid, setIsDateValid] = useState(true);
  const [addressError, setAddressError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [officeTypeError, setOfficeTypeError] = useState(false);

  const [locationNameErrorMessage, setLocationNameErrorMessage] =
    useState(false);
  const [addressErrorMessage, setAddressErrorMessage] = useState(false);
  const [postalCodeErrorMessage, setPostalCodeErrorMessage] = useState(false);
  const [cityErrorMessage, setCityErrorMessage] = useState(false);
  const [stateErrorMessage, setStateErrorMessage] = useState(false);
  const [countryErrorMessage, setCountryErrorMessage] = useState(false);
  const [officeTypeErrorMessage, setOfficeTypeErrorMessage] = useState(false);
  const [officeLocation, setOfficeLocation] = useState([]);

  const dispatch = useDispatch();
  const _axios = HttpService.getAxiosClient();

  const errorMessage = React.useMemo(() => {
    switch (error) {
      case 'invalidDate': {
        setIsDateValid(false);
        return 'Invalid date';
      }
      default: {
        setIsDateValid(true);
        return '';
      }
    }
  }, [error]);

  const fetchOfficeLocation = async () => {
    setOfficeLocation([]);
    try {
      const response = await _axios.get(`${ADMIN_PATH}/office-location`);
      if (response.status === 200) setOfficeLocation(response.data.data);
    } catch (error) {
      console.error('Error fetching office location:', error);
    }
  };

  const handleCancelForm = () => {
    setExpanded(!expanded);

    setLocationName('');
    setAddress('');
    setPostalCode('');
    setCity('');
    setState('');
    setCountry('');
    setOfficeType('');
    setOpeningDate(new Date());

    setLocationNameError('');
    setAddressError('');
    setPostalCodeError('');
    setCityError('');
    setStateError('');
    setCountryError('');
    setOfficeTypeError('');

    setLocationNameErrorMessage('');
    setAddressErrorMessage('');
    setPostalCodeErrorMessage('');
    setCityErrorMessage('');
    setStateErrorMessage('');
    setCountryErrorMessage('');
    setOfficeTypeErrorMessage('');

    setEditable(false);
  };

  const officeLocationDataValidation = () => {
    const trimmedLocationName = locationName.trim();
    const trimmedAddress = address.trim();
    const trimmedCity = city.trim();
    const trimmedState = state.trim();
    const trimmedCountry = country.trim();
    const trimmedPostalCode = postalCode.trim();
    const trimmedOfficeType = officeType.trim();
    if (trimmedLocationName === '') {
      setLocationNameError(true);
      setLocationNameErrorMessage('Location name is required');
    }
    if (trimmedAddress === '') {
      setAddressError(true);
      setAddressErrorMessage('Building, Street, Area is required');
    }
    if (trimmedCity === '') {
      setCityError(true);
      setCityErrorMessage('City is required');
    }
    if (trimmedState === '') {
      setStateError(true);
      setStateErrorMessage('State is required');
    }
    if (trimmedCountry === '') {
      setCountryError(true);
      setCountryErrorMessage('Country is required');
    }
    if (trimmedPostalCode === '') {
      setPostalCodeError(true);
      setPostalCodeErrorMessage('Postal Code is required');
    }
    if (trimmedOfficeType === '') {
      setOfficeTypeError(true);
      setOfficeTypeErrorMessage('Office Type is required');
    }
    if (locationNameError) {
      return false;
    }
    if (
      trimmedLocationName &&
      trimmedAddress &&
      trimmedCity &&
      trimmedState &&
      trimmedCountry &&
      trimmedPostalCode &&
      trimmedOfficeType &&
      openingDate &&
      isDateValid
    ) {
      return true;
    }
  };

  const addOfficeLocation = async (payload) => {
    try {
      const response = await _axios.post(
        `${ADMIN_PATH}/office-location`,
        payload,
      );
      return ErrorSwitchCase(response);
    } catch (error) {
      return ErrorSwitchCase(error.response.data);
    }
  };

  const updateOfficeLocation = async (payload) => {
    try {
      const response = await _axios.put(
        `${ADMIN_PATH}/office-location`,
        payload,
      );
      return ErrorSwitchCase(response);
    } catch (error) {
      return ErrorSwitchCase(error.response.data);
    }
  };

  const handleSubmitForOfficeLocation = async () => {
    if (officeLocationDataValidation()) {
      setLoadingApiCall(true);
      const dummyPayload = {
        tenantId: process.env.REACT_APP_TENANT_ID,
        locationName: locationName.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        country: country.trim(),
        postalCode: postalCode.trim(),
        officeType: officeType.trim(),
        openingDate: moment(new Date(openingDate)).format('YYYY-MM-DD'),
        status: 'ACTIVE',
      };
      const result = await addOfficeLocation(dummyPayload);
      setLoadingApiCall(false);
      setLocationName('');
      setAddress('');
      setPostalCode('');
      setCity('');
      setState('');
      setCountry('');
      setOfficeType('');
      setExpanded(!expanded);
      setLocationNameError('');
      setAddressError('');
      setPostalCodeError('');
      setCityError('');
      setStateError('');
      setCountryError('');
      setOfficeTypeError('');

      setLocationNameErrorMessage('');
      setAddressErrorMessage('');
      setPostalCodeErrorMessage('');
      setCityErrorMessage('');
      setStateErrorMessage('');
      setCountryErrorMessage('');
      setOfficeTypeErrorMessage('');
      loadData();
      dipatchNotification(result.type, result.message);
    } else {
      window.scrollTo(0, 0);
    }
  };

  const officeEditable = async () => {
    if (officeLocationDataValidation) {
      setLoadingApiCall(true);
      const payload = {
        id: office.id,
        tenantId: process.env.REACT_APP_TENANT_ID,
        locationName: locationName.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        country: country.trim(),
        postalCode: postalCode.trim(),
        officeType: officeType.trim(),
        openingDate: moment(new Date(openingDate)).format('YYYY-MM-DD'),
        status: 'ACTIVE',
      };
      const result = await updateOfficeLocation(payload);
      setLoadingApiCall(false);
      setLocationName('');
      setAddress('');
      setPostalCode('');
      setCity('');
      setState('');
      setCountry('');
      setOfficeType('');
      setExpanded(!expanded);
      setLocationNameError('');
      setAddressError('');
      setPostalCodeError('');
      setCityError('');
      setStateError('');
      setCountryError('');
      setOfficeTypeError('');

      setLocationNameErrorMessage('');
      setAddressErrorMessage('');
      setPostalCodeErrorMessage('');
      setCityErrorMessage('');
      setStateErrorMessage('');
      setCountryErrorMessage('');
      setOfficeTypeErrorMessage('');

      setEditable(false);

      loadData();
      dipatchNotification(result.type, result.message);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  useEffect(() => {
    if (office) {
      setLocationName(office.locationName);
      setOpeningDate(office.openingDate);
      setAddress(office.address);
      setPostalCode(office.postalCode);
      setCity(office.city);
      setState(office.state);
      setCountry(office.country);
      setOfficeType(office.officeType);
    }
  }, [office]);

  useEffect(() => {
    fetchOfficeLocation();
  }, []);

  const handleLocationNameChange = (e) => {
    const value = e.target.value;
    setLocationName(value);

    const isDuplicate = officeLocation.some(
      (location) =>
        location?.locationName?.toLowerCase() === value.toLowerCase(),
    );
    if (isDuplicate) {
      setLocationNameError(true);
      setLocationNameErrorMessage('This location already exists.');
    } else {
      setLocationNameError(false);
      setLocationNameErrorMessage('');
    }
  };

  return (
    <>
      <div style={{ marginBottom: '15px' }}>
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          disableGutters
          elevation={0}
        >
          <Grid
            sx={{ backgroundColor: expanded ? '' : '#f7f8fa' }}
            container
            justifyContent="flex-end"
            pb={0}
            pr={0}
          >
            <AccordionSummary>
              {!expanded ? (
                <Button
                  variant="contained"
                  onClick={resetOffice}
                  startIcon={<AddIcon fontSize="small" />}
                >
                  Add Locations
                </Button>
              ) : null}
            </AccordionSummary>
          </Grid>
          <AccordionDetails>
            <Box
              component="form"
              sx={{
                mx: 10,
                mb: 2,
              }}
              noValidate
              autoComplete="off"
            >
              <div className="space-between-container">
                <TextField
                  name="locationName"
                  value={locationName}
                  onChange={handleLocationNameChange}
                  id="location-name"
                  error={locationNameError}
                  helperText={locationNameErrorMessage}
                  label="Location Name"
                  sx={{ width: '300px', margin: 1 }}
                />

                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  sx={{ display: 'none' }}
                >
                  <DatePicker
                    onError={(newError) => setError(newError)}
                    slotProps={{
                      textField: {
                        helperText: errorMessage,
                      },
                    }}
                    label="Opening Date"
                    inputFormat="YYYY-MM-DD"
                    value={openingDate}
                    minDate={dayjs('1990-01-01')}
                    onChange={(newValue) => {
                      setOpeningDate(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        sx={{ width: '300px', margin: 1 }}
                        {...params}
                        error={!isDateValid}
                        helperText={errorMessage}
                      />
                    )}
                  />
                </LocalizationProvider>
              </div>

              <div className="space-between-container">
                <TextField
                  name="address"
                  id="standard-multiline-static"
                  label="Building, Street, Area"
                  sx={{ margin: 1 }}
                  fullWidth
                  value={address}
                  error={addressError}
                  helperText={addressErrorMessage}
                  onChange={(e) => {
                    if (e.target.value) {
                      setAddressError('');
                      setAddressErrorMessage('');
                    }
                    setAddress(e.target.value);
                  }}
                />
              </div>
              <div className="space-between-container">
                <TextField
                  name="postalCode"
                  id="standard-multiline-static"
                  label="Postal Code"
                  sx={{ margin: 1 }}
                  fullWidth
                  value={postalCode}
                  error={postalCodeError}
                  helperText={postalCodeErrorMessage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      setPostalCode(value);
                      setPostalCodeError(false);
                      setPostalCodeErrorMessage('');
                    } else {
                      setPostalCodeError(true);
                      setPostalCodeErrorMessage(
                        'Postal Code must contain only numbers.',
                      );
                    }
                  }}
                />
                <TextField
                  name="city"
                  id="standard-multiline-static"
                  label="City"
                  sx={{ margin: 1 }}
                  fullWidth
                  value={city}
                  error={cityError}
                  helperText={cityErrorMessage}
                  onChange={(e) => {
                    if (e.target.value) {
                      setCityError('');
                      setCityErrorMessage('');
                    }
                    setCity(e.target.value);
                  }}
                />
                <TextField
                  name="state"
                  id="standard-multiline-static"
                  label="State"
                  sx={{ margin: 1 }}
                  fullWidth
                  value={state}
                  error={stateError}
                  helperText={stateErrorMessage}
                  onChange={(e) => {
                    if (e.target.value) {
                      setStateError('');
                      setStateErrorMessage('');
                    }
                    setState(e.target.value);
                  }}
                />
                <TextField
                  name="country"
                  id="standard-multiline-static"
                  label="Country"
                  sx={{ margin: 1 }}
                  fullWidth
                  value={country}
                  error={countryError}
                  helperText={countryErrorMessage}
                  onChange={(e) => {
                    if (e.target.value) {
                      setCountryError('');
                      setCountryErrorMessage('');
                    }
                    setCountry(e.target.value);
                  }}
                />
              </div>
              <div className="space-between-container">
                <TextField
                  name="officeType"
                  id="standard-multiline-static"
                  label="Office Type"
                  sx={{ margin: 1, marginBottom: 2 }}
                  fullWidth
                  value={officeType}
                  error={officeTypeError}
                  helperText={officeTypeErrorMessage}
                  onChange={(e) => {
                    if (e.target.value) {
                      setOfficeTypeError('');
                      setOfficeTypeErrorMessage('');
                    }
                    setOfficeType(e.target.value);
                  }}
                />
              </div>
              <div
                className="space-between-container mb"
                style={{ padding: '8px' }}
              >
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleCancelForm()}
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={loadingApiCall}
                  onClick={() => {
                    editable
                      ? officeEditable()
                      : handleSubmitForOfficeLocation();
                  }}
                  variant="contained"
                >
                  {loadingApiCall ? 'loading...' : 'Save'}
                </LoadingButton>
              </div>
            </Box>
          </AccordionDetails>
        </Accordion>
      </div>
    </>
  );
};

export default AddOfficeLocationComponent;
